import {
  DueDiligenceRecordsListSortBy,
  DueDiligenceRecordsSortDirection,
  FieldConfigurationModalPageGetDueDiligenceRecordQuery,
  Security_Status
} from '@/__generated__/graphql';
import client from '@/apollo-client';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Accordion } from '@/components/basicComponents/accordion';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { AddButton } from '@/components/dealPipeline-page/modify/addButton';
import { defaultListDataWithId } from '@/components/dealPipeline-page/modify/constants';
import { DeleteDynamicItem, DynamicSelectsWrapper, DynamicSelectWrapper } from '@/components/dealPipeline-page/modify/radioWithDynamicSelects';
import { IListDataWithId } from '@/components/dealPipeline-page/modify/types';
import {
  addDynamicSelect,
  deleteDynamicSelect,
  getDynamicSelectPreviewValue,
  onChangeDynamicSelect
} from '@/components/dealPipeline-page/modify/utils';
import { RERUN_AI_SUGGESTION_MUTATION } from '@/components/dueDiligence-page/queries';
import { Loader } from '@/components/loaders/loader';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { safeJsonParse } from '@/shared/utils/json';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { AIFieldType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AiSuggestionsTable } from './aiSuggestionsTable/table';
import AiSuggestionsMobileTable from './aiSuggestionsTable/tableMobile';
import {
  FIELD_CONFIGURATION_MODAL_PAGE_GET_DUE_DILIGENCE_RECORD_QUERY,
  FIELD_CONFIGURATION_MODAL_PAGE_LIST_DUE_DILIGENCE_RECORD_QUERY
} from './queries';
import { IAiSuggestionsTableData } from './types';

const getUserAnswer = ({ value, commentary, isJsonFieldType }: { value: string; commentary: string; isJsonFieldType: boolean }) => {
  if (isJsonFieldType) {
    return safeJsonParse(value);
  }

  const hasValueAndCommentary = value && commentary;
  if (hasValueAndCommentary) {
    return `${value} | ${commentary}`;
  }

  return value || commentary || null;
};

type FieldConfigurationModalPageFieldType =
  FieldConfigurationModalPageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]['fields'][number];

enum GenerateSuggestionsStatus {
  Default = 'Default',
  Success = 'Success',
  Error = 'Error',
  Loading = 'Loading'
}

interface AiTestingSectionProps {
  currentField: IFieldEditor;
  funnelId: string | null;
  funnelStaticId: string | null;
}

export const AiTestingSection = ({ currentField, funnelId, funnelStaticId }: AiTestingSectionProps) => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { searchParams } = useSearchParamsManager();
  const { default: defaultErrorMessage } = useErrorMessages();

  const fieldId = searchParams.get('fieldId') || '';

  const [recordFieldsCache, setRecordFieldsCache] = useState<Record<string, FieldConfigurationModalPageFieldType[]>>({});
  const [strategies, setStrategies] = useState<IListDataWithId>(defaultListDataWithId);
  const [tableData, setTableData] = useState<IAiSuggestionsTableData[]>([]);
  const [generateSuggestionsStatus, setGenerateSuggestionsStatus] = useState<GenerateSuggestionsStatus>(GenerateSuggestionsStatus.Default);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState<string | null>(null);

  const [rerunAISuggestion] = useMutation(RERUN_AI_SUGGESTION_MUTATION);

  useEffect(() => {
    if (!strategies.allList.length) return;

    const loadAITestingData = async () => {
      const savedAITestingRecordIds: string[] = safeJsonParse(sessionStorage.getItem('aiTestingRecordIds')) || [];
      const filteredSavedAITestingRecordIds = savedAITestingRecordIds?.filter((recordId) =>
        strategies.allList.some((strategy) => strategy.value === recordId)
      );

      if (filteredSavedAITestingRecordIds?.length) {
        const isCurrentRecordInSelected = filteredSavedAITestingRecordIds.includes(currentField.recordId);
        const selectedRecordIds = isCurrentRecordInSelected
          ? filteredSavedAITestingRecordIds
          : [currentField.recordId, ...filteredSavedAITestingRecordIds];

        setStrategies((prevStrategies) => ({
          ...prevStrategies,
          selected: selectedRecordIds
        }));

        const tableDataLoadPromises = selectedRecordIds.map(async (recordId) => await getTableData({ recordId }));
        await Promise.all(tableDataLoadPromises);
        return;
      }

      if (!currentField?.recordId || strategies.selected.includes(currentField.recordId) || filteredSavedAITestingRecordIds?.length) {
        return;
      }
      setStrategies((prevStrategies) => ({ ...prevStrategies, selected: [currentField.recordId] }));
      await getTableData({ recordId: currentField.recordId });
    };

    loadAITestingData();
  }, [currentField?.recordId, strategies.allList]);

  useEffect(() => {
    if (!fieldId || !strategies.selected.length) return;
    const loadAITestingData = async () => {
      setTableData([]);
      const tableDataLoadPromises = strategies.selected.map(async (recordId) => await getTableData({ recordId }));
      await Promise.all(tableDataLoadPromises);
    };

    loadAITestingData();
  }, [fieldId]);

  useEffect(() => {
    const filteredTableData = tableData.filter((el) => strategies.selected.includes(el.recordId));
    setTableData(filteredTableData);

    if (strategies.selected.length) {
      sessionStorage.setItem('aiTestingRecordIds', JSON.stringify(strategies.selected));
    }

    setStrategies((prevStrategies) => {
      return {
        ...prevStrategies,
        list: prevStrategies.list
          .filter((strategy) => !prevStrategies.selected.includes(strategy.value))
          .sort((a, b) => a.label.localeCompare(b.label))
      };
    });
  }, [strategies.selected]);

  const { loading: listDueDiligenceRecordsLoading } = useQuery(FIELD_CONFIGURATION_MODAL_PAGE_LIST_DUE_DILIGENCE_RECORD_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      data: {
        funnelStaticIdFilter: [funnelStaticId],
        sortBy: DueDiligenceRecordsListSortBy.InvestmentName,
        sortDirection: DueDiligenceRecordsSortDirection.Asc,
        statusFilter: [
          Security_Status.Lead,
          Security_Status.Declined,
          Security_Status.DueDiligence,
          Security_Status.Approved,
          Security_Status.Rejected
        ],
        limit: 1000
      }
    },
    onCompleted: (data) => {
      const strategiesList = data.listDueDiligenceRecords.records
        .filter((record) => record?.activeTier?.funnel?.id === funnelId)
        .map((record) => createSimpleSelectOption(record.id, record.investment.name));
      setStrategies({ ...strategies, selected: [], allList: strategiesList, list: strategiesList });
    }
  });

  const getRecordFields = async ({
    recordId,
    withTableLoader = true
  }: {
    recordId: string;
    withTableLoader?: boolean;
  }): Promise<FieldConfigurationModalPageFieldType[]> => {
    try {
      setIsTableLoading(withTableLoader);

      const { data } = await client.query({
        query: FIELD_CONFIGURATION_MODAL_PAGE_GET_DUE_DILIGENCE_RECORD_QUERY,
        variables: {
          id: recordId
        }
      });

      const { funnel } = data.getDueDiligenceRecord;

      const allFields = funnel?.tiers?.flatMap((tier) => tier?.sections || [])?.flatMap((section) => section?.fields || []);
      setRecordFieldsCache((prev) => ({ ...prev, [recordId]: allFields }));

      return allFields;
    } catch (error) {
      console.error(error, 'error');
    } finally {
      setIsTableLoading(false);
    }
  };

  const getTableData = async ({
    recordId,
    skipCache = false,
    withTableLoader = true
  }: {
    recordId: string;
    skipCache?: boolean;
    withTableLoader?: boolean;
  }) => {
    let allFields: FieldConfigurationModalPageFieldType[] = [];

    const isRecordFieldsInCache = recordFieldsCache?.[recordId];

    if (isRecordFieldsInCache && !skipCache) {
      allFields = isRecordFieldsInCache;
    } else {
      allFields = await getRecordFields({ recordId, withTableLoader });
    }

    const matchingField = allFields?.find((field) => field.id === currentField.fieldId);
    if (matchingField) {
      const { id: fieldId, type: fieldType, AI, data, promptGroup } = matchingField;
      const strategyName = strategies.allList.find((strategy) => strategy.value === recordId)?.label || '';
      const isJsonFieldType = fieldType === getEnumKeyByValue(AIFieldType, AIFieldType.JSON);

      const aiSuggestion = (isJsonFieldType ? safeJsonParse(AI.answerJson) : AI?.answers?.[0]) || null;
      const answer = getUserAnswer({ value: data?.value, commentary: data?.commentary, isJsonFieldType });

      const tableRowData: IAiSuggestionsTableData = {
        recordId,
        fieldId,
        fieldType,
        fieldAnswerId: data?.id,
        promptGroupId: promptGroup?.id,
        strategy: strategyName,
        aiSuggestion,
        answer,
        isPreviousAiRunExists: !!AI
      };

      setTableData((prevData) => {
        const existingIndex = prevData.findIndex((row) => row.recordId === tableRowData.recordId);

        if (existingIndex !== -1) {
          const newData = [...prevData];
          newData[existingIndex] = tableRowData;
          return newData;
        } else {
          return [tableRowData, ...prevData];
        }
      });
    }
  };

  const handleChangeDynamicSelect = async (value: string, index: number) => {
    if (!value) return;
    onChangeDynamicSelect(value, index, strategies, setStrategies);
    await getTableData({ recordId: value });
  };

  const handleGenerateSuggestions = async () => {
    try {
      setGenerateSuggestionsStatus(GenerateSuggestionsStatus.Loading);

      const rowsWithPromptGroup = tableData.filter((row) => row.promptGroupId && row.isPreviousAiRunExists);

      if (rowsWithPromptGroup.length === 0) {
        setGenerateSuggestionsStatus(GenerateSuggestionsStatus.Error);
        return;
      }

      const suggestionPromises = rowsWithPromptGroup.map(async (row) => {
        await rerunAISuggestion({
          variables: {
            data: {
              fieldAnswerId: row.fieldAnswerId,
              fieldId: row.fieldId,
              promptGroupId: row.promptGroupId,
              recordId: row.recordId
            }
          }
        });
        return row.recordId;
      });

      const completedRecordIds = (await Promise.all(suggestionPromises)).filter(Boolean);
      const tableDataRefreshPromises = completedRecordIds.map(
        async (recordId) => await getTableData({ recordId, skipCache: true, withTableLoader: false })
      );
      await Promise.all(tableDataRefreshPromises);

      setGenerateSuggestionsStatus(GenerateSuggestionsStatus.Success);
    } catch (error) {
      setGenerateSuggestionsStatus(GenerateSuggestionsStatus.Error);
    } finally {
      setUpdatingRowId(null);
    }
  };

  const openEditFieldPage = ({ recordId, fieldType, fieldId }: { recordId: string; fieldType: string; fieldId: string }) => {
    navigate(
      `/dueDiligence/${recordId}?editFieldPageType=${fieldType}&fieldId=${fieldId}&fieldEditorModalPageType=default&returnToPage=fieldConfiguration`,
      { replace: true }
    );
  };

  if (listDueDiligenceRecordsLoading) return <Loader />;

  return (
    <Accordion title="AI Testing">
      <div>
        <Banner
          title=""
          description={
            <>
              Select up to 5 records with recent AI Suggestions. You can generate a new AI Suggestion for this field (
              <b>{currentField?.name || ''}</b>) to ensure that your prompt works well in different contexts.
            </>
          }
          icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
          bgColor={themeVar('layer.base')}
          color={themeVar('font.base')}
          dividerColor={themeVar('border.base')}
        />
        <CustomLabel>Select Strategies</CustomLabel>
        <TwoColumnWrapper isMobile={isMobile}>
          <DynamicSelectsWrapper>
            {strategies.selected.map((el, index) => (
              <DynamicSelectWrapper key={`advisoryFirm-${index}`}>
                <Select
                  value={strategies.selected[index]}
                  previewValue={getDynamicSelectPreviewValue(strategies.selected[index], strategies.allList)}
                  setValue={(value) => handleChangeDynamicSelect(value, index)}
                  options={strategies.list}
                  styleType={
                    generateSuggestionsStatus === GenerateSuggestionsStatus.Loading || strategies.selected[strategies.selected.length - 1] !== el
                      ? 'disabled'
                      : 'default'
                  }
                  size="md"
                  withSearch
                />
                {generateSuggestionsStatus !== GenerateSuggestionsStatus.Loading && (
                  <DeleteDynamicItem onClick={() => deleteDynamicSelect(index, strategies, setStrategies)}>
                    <img src="/static/icons/blue-cross.svg" alt="cross" />
                  </DeleteDynamicItem>
                )}
              </DynamicSelectWrapper>
            ))}
            {generateSuggestionsStatus !== GenerateSuggestionsStatus.Loading &&
            strategies.list.length &&
            strategies.selected[strategies.selected.length - 1] !== '' ? (
              <AddButton text="Add" onClick={() => addDynamicSelect(setStrategies)} />
            ) : (
              <></>
            )}
          </DynamicSelectsWrapper>
        </TwoColumnWrapper>
      </div>
      <ButtonWrapper>
        <Button
          size="md"
          styleType={
            generateSuggestionsStatus !== GenerateSuggestionsStatus.Loading &&
            strategies.selected.length &&
            strategies.selected?.[strategies.selected.length - 1] !== ''
              ? 'default'
              : 'disabled'
          }
          isLoading={generateSuggestionsStatus === GenerateSuggestionsStatus.Loading}
          onClick={handleGenerateSuggestions}
        >
          Generate Suggestions
        </Button>
      </ButtonWrapper>
      {generateSuggestionsStatus === GenerateSuggestionsStatus.Success && (
        <BannerWrapper isMobile={isMobile}>
          <Banner
            icon={<ApprovedIcon fill={themeVar('context.light')} />}
            title="Success!"
            description="Suggestions ready for review."
            bgColor={themeVar('context.success')}
            onClose={() => setGenerateSuggestionsStatus(GenerateSuggestionsStatus.Default)}
          />
        </BannerWrapper>
      )}
      {generateSuggestionsStatus === GenerateSuggestionsStatus.Error && (
        <BannerWrapper isMobile={isMobile}>
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={defaultErrorMessage}
            bgColor={themeVar('context.error')}
          />
        </BannerWrapper>
      )}
      <div>
        <CustomLabel>AI Suggestions</CustomLabel>
        {isMobile ? (
          <AiSuggestionsMobileTable
            tableData={tableData}
            loading={isTableLoading}
            updatingRowId={updatingRowId}
            openEditFieldPage={openEditFieldPage}
          />
        ) : (
          <AiSuggestionsTable tableData={tableData} loading={isTableLoading} updatingRowId={updatingRowId} openEditFieldPage={openEditFieldPage} />
        )}
      </div>
    </Accordion>
  );
};

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: 20px;
  width: 100%;
`;

const CustomLabel = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  padding: 20px 0 10px;
  color: ${themeVar('font.strong')};
`;

const ButtonWrapper = styled.div`
  display: flex;
  padding: 10px 0;
`;

const BannerWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
`;
